import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { find, isNil, some } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSubscriptionsInfo, useSubscriptionsItems, useTelemetryCreditItems } from '@/api/subscriptions';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { TelemetryTransactionSummary } from '@/components/subscriptions/TelemetryTransactionSummary';
import { RecurringCreditsSetupTip } from '@/components/transactions/RecurringCreditsSetupTip';
import { TransactionIntentFooter } from '@/components/transactions/TransactionIntentFooter';
import { TransactionLineChangeSummary } from '@/components/transactions/TransactionLineChangeSummary';
import { TransactionModal } from '@/components/transactions/TransactionModal';
import { RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import { useTransactionContext } from '@/context/transaction';
import {
    findByTelemetryLinePriceLookupKey,
    useTelemetryLineChanges,
} from '@/context/transaction/TelemetryLineChangeContext';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';

export const DeviceTelemetryTransactionManagement = ({
    isManageMode,
    onEndManagement,
}: {
    isManageMode: boolean;
    onEndManagement: () => void;
}) => {
    const [isConfirmingTransaction, setConfirmingTransaction] = useState(false);
    const { setTelemetryLineChanges } = useTelemetryLineChanges();
    const {
        totalDue,
        updateSubscription,
        hasChanges,
        mutationStatus: { isSuccess } = {},
    } = useTransactionContext('subscription');
    const needsConfirmationStep = !isConfirmingTransaction && totalDue !== 0;

    useUnsavedChangesGuard(isManageMode && hasChanges, 'telemetry-lines');

    const handleExitTransaction = useCallback(() => {
        setConfirmingTransaction(false);
        setTelemetryLineChanges({});
        onEndManagement();
    }, [onEndManagement, setTelemetryLineChanges]);

    function handleConfirm() {
        if (needsConfirmationStep) {
            setConfirmingTransaction(true);
        } else {
            updateSubscription();
        }
    }

    useEffect(() => {
        if (isSuccess) {
            // return to the base telemetry page after success so we don't see weird no-op transactions
            // show up behind the confirmation dialog
            handleExitTransaction();
        }
    }, [isSuccess, handleExitTransaction]);

    useEffect(() => {
        let bodyOverflow: string | undefined = undefined;
        if (isConfirmingTransaction && !(document.body.style.overflow === 'hidden')) {
            bodyOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        return () => {
            if (!isNil(bodyOverflow)) {
                document.body.style.overflow = bodyOverflow;
            }
        };
    }, [isConfirmingTransaction]);

    return (
        <>
            {isConfirmingTransaction && (
                /* Since we have the footer outside the modal we need to be a little fussy to make sure the transaction modal stays visible.
                    80px is the tallest the footer gets upon wrap. */
                <Paper sx={{ position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, overflow: 'auto', pb: '80px' }}>
                    <TransactionModal onBackClick={() => setConfirmingTransaction(false)}>
                        <TelemetryTransactionSummary />
                    </TransactionModal>
                </Paper>
            )}
            {isManageMode && (
                <TransactionIntentFooter
                    confirmLabel={needsConfirmationStep ? 'Continue' : undefined /* Default depending on txn total */}
                    canConfirm={
                        needsConfirmationStep
                            ? hasChanges /* Proceed even without payment if we're doing another step */
                            : undefined /* If we're ready to confirm, let the default logic decide if it's possble */
                    }
                    onCancel={handleExitTransaction}
                    onConfirm={handleConfirm}
                    sx={{ position: 'sticky' }}
                >
                    <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', flexGrow: 1 }}>
                        <TransactionLineChangeSummary />
                        {needsConfirmationStep && (
                            <Typography variant="minorLabel" sx={{ mx: 1 }}>
                                Price breakdown shown on next step
                            </Typography>
                        )}
                    </Stack>
                </TransactionIntentFooter>
            )}
            <TelemetryUpdateConfirmationDialog />
        </>
    );
};

export const TelemetryUpdateConfirmationDialog = ({
    onDone,
    ...props
}: { onDone?: () => void } & Omit<DialogProps, 'open' | 'onClose'>) => {
    const { isRetainExtraLines, numExtraLines } = useTelemetryLineChanges();
    const { data: lineSubItems } = useSubscriptionsItems();
    const { data: satCreditItems } = useTelemetryCreditItems();
    const [wasCancellation, setCancellation] = useState(false);

    const {
        isPending: contextPending,
        mutationStatus: { error, isSuccess, isPending, isIdle, reset } = {},
        totalDue,
        isCancellation,
        lineItemQuantities,
    } = useTransactionContext('subscription');
    const { data: subscriptionInfo } = useSubscriptionsInfo({
        status: wasCancellation ? 'latest' : 'current',
    });
    const [titleNoun, setTitleNoun] = useState<string>();
    const [expectReceipt, setExpectReceipt] = useState(false);

    useEffect(() => {
        if (contextPending || isIdle) return;

        if (isNil(titleNoun) && !!totalDue) {
            // commit it in an effect rather than on-demand so that we don't revert to "Telemetry change" after the transaction
            // has been committed and the remaining due goes to zero.
            setTitleNoun(totalDue < 0 ? 'Account credit' : 'Purchase');
            setExpectReceipt(true);
        }
    }, [contextPending, isIdle, titleNoun, totalDue]);

    useEffect(() => {
        if (contextPending || isIdle) return;

        if (isCancellation) {
            setCancellation(isCancellation);
            setExpectReceipt(true);
        }
    }, [contextPending, isIdle, isCancellation]);

    const showRecurringCreditsTip = useMemo(() => {
        const iridiumSubscriptionItem = findByTelemetryLinePriceLookupKey(lineSubItems?.data, 'iridium', 'month');
        const satCreditsItem = find(satCreditItems, {
            price_lookup_key: RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY,
        });
        if (!iridiumSubscriptionItem || !satCreditsItem || !lineItemQuantities || !subscriptionInfo?.data.length) {
            return false;
        }

        const iridiumQty = lineItemQuantities[iridiumSubscriptionItem.price_id] ?? 0;
        const satCreditsQty = find(subscriptionInfo?.data?.[0].items, { price_id: satCreditsItem.price_id }) ?? 0;

        return iridiumQty > 0 && satCreditsQty === 0;
    }, [lineItemQuantities, lineSubItems?.data, satCreditItems, subscriptionInfo?.data]);

    const title = useMemo(() => {
        if (wasCancellation) {
            return isSuccess
                ? 'Your recurring subscription has been successfully cancelled'
                : error
                  ? 'Cancellation unsuccessful'
                  : 'Cancellation processing...';
        }
        return `${titleNoun ?? 'Telemetry change'} ${isSuccess ? ' successful' : error ? ' unsuccessful' : ' processing...'}`;
    }, [error, isSuccess, titleNoun, wasCancellation]);

    function handleClickDone() {
        reset?.();
        onDone?.();
        setExpectReceipt(false);
        setTitleNoun(undefined);
    }

    if (contextPending || isIdle) {
        // ruling out both idle and context not ready
        return false;
    }

    const retainedUnusedLines = isRetainExtraLines && some(numExtraLines, (n) => n > 0);

    return (
        <Dialog open={true} onClose={() => {}} disableScrollLock maxWidth="md" {...props}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {isPending && <GenericSkeleton lines={2} />}
                {error && <Alert severity="error">{error.message}</Alert>}
                {isSuccess &&
                    (wasCancellation ? (
                        <Typography>
                            You will no longer be charged monthly. All telemetry lines have been deactivated. You can
                            resume service at any time by re-enabling device telemetry.
                        </Typography>
                    ) : (
                        <>
                            <Typography>
                                It may take up to 10 minutes for device telemetry to transition.
                                {retainedUnusedLines && (
                                    <>
                                        {' '}
                                        Your disabled lines will remain in your subscription and are immediately
                                        available for devices to use. You will continue to pay for these lines on a
                                        monthly basis.
                                    </>
                                )}
                            </Typography>
                            {showRecurringCreditsTip && <RecurringCreditsSetupTip />}
                        </>
                    ))}
            </DialogContent>
            <DialogActions>
                {isSuccess && (
                    <>
                        {expectReceipt && subscriptionInfo?.data[0]?.hosted_invoice_url && (
                            <Button target="_blank" href={subscriptionInfo.data[0].hosted_invoice_url}>
                                View receipt
                            </Button>
                        )}
                        <Button onClick={handleClickDone} autoFocus variant="contained">
                            Done
                        </Button>
                    </>
                )}
                {error && (
                    <Button onClick={() => reset?.()} autoFocus variant="contained">
                        Back
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};
